import { supabase } from "@/integrations/supabase/client";
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_PUBLISHABLE_KEY } from "@/integrations/external-supabase/client";

const CHAT_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kin-chat`;
const POST_CALL_SETTLE_MS = 1500;

/**
 * v1.3 spec §B.7/§B.9/§B.10/§B.13 ("existing Brain Dump fact-capture
 * pipeline"). That pipeline is kin-chat's own [LEARN]-tag fact capture —
 * there is no standalone extraction endpoint. This sends the card's raw
 * text through kin-chat as a background call: never appended to the visible
 * chat transcript, never persisted to conversations/messages. kin-chat
 * writes any captured facts to family_context itself (source 'chat' or
 * 'chat_tool'); afterward we relabel whatever it just created to
 * source='pro_onboarding' to match spec, and the caller uses the returned
 * count to decide whether to show that card's receipt line.
 *
 * Known limitations, both inherent to reusing kin-chat unmodified (no prompt
 * changes made here — see spec §C / issue H5):
 * - kin-chat caps at 2 [LEARN] tags per response, so a freeform entry
 *   describing 3+ facts in one send only captures 2.
 * - Only rows freshly INSERTed are caught by the learned_at filter below;
 *   if the text merely corrects an existing fact's value, kin-chat UPDATEs
 *   the existing row in place and it won't be relabeled/counted.
 */
export async function runBackgroundFactCapture({
  text,
  userId,
  accessToken,
  googleCalConnected,
}: {
  text: string;
  userId: string;
  accessToken: string;
  googleCalConnected: boolean;
}): Promise<{ factsCaptured: number; captureFailed: boolean }> {
  const beforeIso = new Date().toISOString();
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  try {
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        messages: [{ role: "user", content: text }],
        currentDate,
        user_id: userId,
        google_cal_connected: googleCalConnected,
      }),
    });

    if (resp.body) {
      // Drain fully without using the tokens — kin-chat writes [LEARN] facts
      // in its `finally` block, which runs after the stream closes.
      const reader = resp.body.getReader();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done } = await reader.read();
        if (done) break;
      }
    }
  } catch (e) {
    console.error("[proSetupFlow] background fact capture call failed", e);
    return { factsCaptured: 0, captureFailed: true };
  }

  // Give kin-chat's post-stream write a moment to land before we look for it.
  await new Promise((resolve) => setTimeout(resolve, POST_CALL_SETTLE_MS));

  try {
    const { data, error } = await supabase
      .from("family_context")
      .select("id")
      .eq("user_id", userId)
      .eq("archived", false)
      .in("source", ["chat", "chat_tool"])
      // #1210 — was `created_at`, which does not exist on family_context. Every
      // call 42703'd, the error was folded into the zero-rows branch below, and
      // the receipt silently reported 0 facts captured on every Pro setup since
      // PR #137. The creation timestamp on this table is `learned_at`.
      .gte("learned_at", beforeIso);

    // #1210 — an error and "nothing was captured" are NOT the same outcome and
    // must not share a branch. Folding them together is what made the 42703
    // invisible: a broken query and an empty result produced identical output.
    if (error) {
      console.error("[proSetupFlow] post-call fact lookup failed", error);
      return { factsCaptured: 0, captureFailed: true };
    }
    if (!Array.isArray(data) || data.length === 0) {
      return { factsCaptured: 0, captureFailed: false };
    }

    const ids = data.map((row: { id: string }) => row.id);
    const { error: relabelError } = await supabase
      .from("family_context")
      .update({ source: "pro_onboarding" })
      .in("id", ids);
    // Reported, but factsCaptured still counts them and the receipt still
    // prints — correctly. The facts DID land; only their `source` label is
    // wrong (they stay 'chat'). Unlike every other write in this flow, a
    // failure here doesn't make the "✓" a lie, so it must not suppress it.
    if (relabelError) {
      console.error("[proSetupFlow] post-call source relabel failed", relabelError);
    }

    return { factsCaptured: ids.length, captureFailed: false };
  } catch (e) {
    console.error("[proSetupFlow] post-call source relabel threw", e);
    return { factsCaptured: 0, captureFailed: true };
  }
}
